import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'

import { contentManagementActions, contentManagementSelectors } from 'apps/cms/store'
import { CreateAndSelectUntitledFlowSources, FlowPickerSource } from 'apps/flowPicker/lib/constants'
import { FlowPickerSelectors, FlowPickerActions } from 'apps/flowPicker/store'
import { FSSmartFolders } from 'common/cms/models/FSObject/constants'
import { navigatePollyfill } from 'utils/router/tools'
import { getFlowUrlByPath } from 'utils/router/tools/cms'

interface UseFlowPickerParams {
  source: FlowPickerSource
  onFlowSelect: (flowId: string) => void
  onFlowCreate?: () => void
}

export const useFlowPicker = ({ source, onFlowSelect, onFlowCreate }: UseFlowPickerParams) => {
  const dispatch = useAppDispatch()

  const selectedFlowId = useAppSelector(FlowPickerSelectors.getSelectedFlowId)
  const isOpen = useAppSelector(FlowPickerSelectors.getIsFlowPickerModalOpen)
  const flowPickerSource = useAppSelector(FlowPickerSelectors.getFlowPickerSource)
  const isFlowCreationStarted = useAppSelector(contentManagementSelectors.getIsFlowCreationStarted)
  const isCreateAndSelectUntitledFlowSource = CreateAndSelectUntitledFlowSources.some(
    (createdFlowSource) => source.includes(createdFlowSource),
  )

  const isSearchOrFilterApplied = useAppSelector(
    contentManagementSelectors.getIsSearchOrFilterApplied,
  )
  const { path } = useAppSelector(contentManagementSelectors.getQuery)

  const createAndSelectUntitledFlow = useCallback(async () => {
    const flowPath =
      (Object.values(FSSmartFolders) as string[]).includes(path) || isSearchOrFilterApplied
        ? '/'
        : path

    const data = await dispatch(
      contentManagementActions.createAutomation({ path: flowPath }),
    ).unwrap()

    if (!data) return

    onFlowSelect(data.flow.ns)

    navigatePollyfill(getFlowUrlByPath(data.fs_object.path))
  }, [path, isSearchOrFilterApplied, dispatch, onFlowSelect])

  useEffect(() => {
    if (isOpen && flowPickerSource === source && selectedFlowId) {
      onFlowSelect(selectedFlowId)
      dispatch(FlowPickerActions.closeModal())
    }
  }, [isOpen, flowPickerSource, selectedFlowId, source, onFlowSelect, dispatch])

  useEffect(() => {
    if (isOpen && flowPickerSource === source && isFlowCreationStarted) {
      if (isCreateAndSelectUntitledFlowSource) {
        createAndSelectUntitledFlow()
      }

      if (onFlowCreate) {
        onFlowCreate()
      }

      dispatch(FlowPickerActions.closeModal())
    }
  }, [
    isOpen,
    flowPickerSource,
    isFlowCreationStarted,
    source,
    onFlowCreate,
    dispatch,
    createAndSelectUntitledFlow,
    isCreateAndSelectUntitledFlowSource,
  ])
}
